/**
 * Persistence (architecture §4): storage, envelope, migrations, the load pipeline, the writer and the export code.
 * `newSaveSeed()` is the only place outside the debug fixtures generator allowed to call `Math.random` (§3.5).
 */
export { browserStorage, memoryStorage, StorageFullError, type KeyValueStorage } from './storage';
export { checksumOf, parseEnvelope, wrapEnvelope } from './envelope';
export { CURRENT_SCHEMA, ENVELOPE_MIGRATIONS, migrateEnvelope, type EnvelopeMigrations } from './migrations';
export { createFreshState, keepCorrupt, loadSave, migrateSlice, normalizeState, type AnySlice, type LoadResult, type NormalizeResult } from './load';
export { SaveWriter, SAVE_DEBOUNCE_MS, ROTATE_EVERY_MS, SAVE_WARN_BYTES, SAVE_MAX_BYTES, type SaveResult } from './persist';
export { decodeSaveCode, encodeSaveCode } from './exportCode';

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** A fresh per-save root seed (architecture §3.5): 12 Crockford base32 chars from `crypto` (or Math.random). */
export function newSaveSeed(): string {
  const bytes = new Uint8Array(12);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  let s = '';
  for (const b of bytes) s += CROCKFORD[b & 31];
  return s;
}

/** RFC 4122 v4 UUID for `account.localId` (monetization §11.10: random, no personal data). */
export function newLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
